/**
 * Privacy Dashboard Component
 * 
 * Displays privacy information, encryption status, and data controls
 */

import { View, ScrollView, Pressable, Switch, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { useState } from 'react';

type PrivacyDashboardProps = {
  visible?: boolean;
  onClose?: () => void;
};

export function PrivacyDashboard({ visible = true, onClose }: PrivacyDashboardProps) {
  const theme = useTheme();
  const [cloudBackupEnabled, setCloudBackupEnabled] = useState(true);

  const content = (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ padding: theme.spacing(2), gap: theme.spacing(2) }}
    >
        <Card variant="outlined">
          <View style={{ gap: theme.spacing(2) }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5) }}>
              <Ionicons name="shield-checkmark" size={24} color={theme.colors.primary} />
              <SubHeading>Privacy & Security</SubHeading>
            </View>
            <Body color={theme.colors.textSecondary}>
              Your data is encrypted and secure. We can't see your data — only you can.
            </Body>
          </View>
        </Card>

        <Card variant="outlined">
          <View style={{ gap: theme.spacing(2) }}>
            <SubHeading>Data Encryption</SubHeading>
            <View style={{ gap: theme.spacing(1.5) }}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.background,
                  borderRadius: theme.radii.md,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons name="lock-closed" size={20} color={theme.colors.success || '#34C759'} />
                  <Body>Documents</Body>
                </View>
                <View
                  style={{
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: theme.spacing(0.5),
                    borderRadius: theme.radii.sm,
                    backgroundColor: theme.colors.success + '20',
                  }}
                >
                  <Caption color={theme.colors.success || '#34C759'} style={{ fontWeight: '600' }}>
                    Encrypted
                  </Caption>
                </View>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.background,
                  borderRadius: theme.radii.md,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons name="lock-closed" size={20} color={theme.colors.success || '#34C759'} />
                  <Body>Personal Information</Body>
                </View>
                <View
                  style={{
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: theme.spacing(0.5),
                    borderRadius: theme.radii.sm,
                    backgroundColor: theme.colors.success + '20',
                  }}
                >
                  <Caption color={theme.colors.success || '#34C759'} style={{ fontWeight: '600' }}>
                    Encrypted
                  </Caption>
                </View>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.background,
                  borderRadius: theme.radii.md,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons name="lock-closed" size={20} color={theme.colors.success || '#34C759'} />
                  <Body>Financial Data</Body>
                </View>
                <View
                  style={{
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: theme.spacing(0.5),
                    borderRadius: theme.radii.sm,
                    backgroundColor: theme.colors.success + '20',
                  }}
                >
                  <Caption color={theme.colors.success || '#34C759'} style={{ fontWeight: '600' }}>
                    Encrypted
                  </Caption>
                </View>
              </View>
            </View>
          </View>
        </Card>

        <Card variant="outlined">
          <View style={{ gap: theme.spacing(2) }}>
            <SubHeading>Data Storage</SubHeading>
            <View style={{ gap: theme.spacing(1.5) }}>
              <View
                style={{
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.background,
                  borderRadius: theme.radii.md,
                }}
              >
                <Body style={{ marginBottom: theme.spacing(0.5) }}>Local Storage</Body>
                <Caption color={theme.colors.textSecondary}>
                  Documents and data stored on your device are encrypted and only accessible by you.
                </Caption>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.background,
                  borderRadius: theme.radii.md,
                }}
              >
                <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                  <Body>Cloud Backup (Optional)</Body>
                  <Caption color={theme.colors.textSecondary}>
                    Encrypted backup to cloud for access across devices
                  </Caption>
                </View>
                <Switch
                  value={cloudBackupEnabled}
                  onValueChange={setCloudBackupEnabled}
                  trackColor={{
                    false: theme.colors.borderMuted,
                    true: theme.colors.primary + '50',
                  }}
                  thumbColor={cloudBackupEnabled ? theme.colors.primary : theme.colors.textSecondary}
                />
              </View>
            </View>
          </View>
        </Card>

        <Card variant="outlined">
          <View style={{ gap: theme.spacing(2) }}>
            <SubHeading>What We Can See</SubHeading>
            <View
              style={{
                padding: theme.spacing(1.5),
                backgroundColor: theme.colors.background,
                borderRadius: theme.radii.md,
                gap: theme.spacing(1),
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(1) }}>
                <Ionicons name="checkmark-circle" size={20} color={theme.colors.success || '#34C759'} />
                <Body style={{ flex: 1 }}>
                  We can see that you have trips, but not the details (destinations, dates, etc.)
                </Body>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(1) }}>
                <Ionicons name="checkmark-circle" size={20} color={theme.colors.success || '#34C759'} />
                <Body style={{ flex: 1 }}>
                  We can see usage statistics (features used, not personal data)
                </Body>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(1) }}>
                <Ionicons name="close-circle" size={20} color={theme.colors.error || '#FF3B30'} />
                <Body style={{ flex: 1 }}>
                  We cannot see your documents, passwords, or financial information
                </Body>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(1) }}>
                <Ionicons name="close-circle" size={20} color={theme.colors.error || '#FF3B30'} />
                <Body style={{ flex: 1 }}>
                  We cannot read your email content or access your accounts
                </Body>
              </View>
            </View>
          </View>
        </Card>

        <Card variant="outlined">
          <View style={{ gap: theme.spacing(2) }}>
            <SubHeading>Your Rights</SubHeading>
            <View style={{ gap: theme.spacing(1) }}>
              <Body>• Export all your data at any time</Body>
              <Body>• Delete your account and all data</Body>
              <Body>• Control what data is shared with collaborators</Body>
              <Body>• Opt out of cloud backup</Body>
            </View>
          </View>
        </Card>

        {onClose && (
          <Button label="Close" variant="secondary" onPress={onClose} />
        )}
      </ScrollView>
  );

  if (onClose) {
    return (
      <Modal
        visible={visible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={onClose}
      >
        <View style={{ flex: 1, backgroundColor: theme.colors.surface }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing(2),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
            }}
          >
            <SubHeading>Privacy & Security</SubHeading>
            <Pressable onPress={onClose}>
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          </View>
          {content}
        </View>
      </Modal>
    );
  }

  return content;
}

